import { defineStore } from 'pinia'
import type {
    AutoZnetworkEnterprise,
    AutoZnetworkEntity,
    AutoZnetworkOrganization,
    Domain,
    Space,
    Theme,
} from '~/utils/types/global'
import { buildAnalyticsSettings } from '~/composables/asc/utility/buildAnalyticsSettings'
import type { DomainEntityType } from '~/utils/types/global/enums'
import { MarketType } from '~/utils/types/global/enums'
import type { Honkr } from '~/utils/types/domain'
import { Bureau } from '~/utils/types/domain/enums'
import { CreditScoreTier } from '~/utils/types/visitor/enums'
import { VDPGalleryType } from '~/utils/types/inventory/enums'
import type { VDPGallery } from '~/utils/types/inventory'

export const useDomainStore = defineStore('domain', {
    state: (): State => ({
        domain: {} as Domain,
        entityType: null,
    }),
    getters: {
        getAutoZnetworkOrgId(state): number | null {
            return state.domain.autoznetwork_organization_id ?? null
        },
        getAutoZnetworkEnterpriseId(state): number | null {
            return state.domain.autoznetwork_enterprise_id ?? null
        },
        autoZnetworkOrganization(state): AutoZnetworkOrganization | null {
            return state.domain.autoz_organization ?? null
        },
        autoZnetworkEnterprise(state): AutoZnetworkEnterprise | null {
            return state.domain.autoz_enterprise ?? null
        },
        autoZnetworkEntity(state): AutoZnetworkEntity {
            return state.domain.autoz_entity
        },
        autoZnetworkSettings(): Record<string, any> {
            return (
                this.autoZnetworkEnterprise?.settings ??
                this.autoZnetworkOrganization?.settings ??
                {}
            )
        },
        marketType(): MarketType {
            return this.autoZnetworkEntity?.market_type ?? MarketType.AUTOMOTIVE
        },
        facetAttributes(state): string[] {
            return state.domain.facet_attributes
        },
        space(state): Space | undefined {
            return state.domain.space
        },
        theme(state): Theme {
            return state.domain.theme
        },
        getDefaultInventoryQuery(state): Record<any, any> {
            return state.domain.default_inventory_query
        },
        getDefaultUnitsQuery(state): Record<any, any> {
            return state.domain.default_units_query
        },
        getInventoryPrecision(): number | null {
            return getSetting(this.settings, 'geo.precision') ?? null
        },
        settings(state): Record<any, any> {
            return state.domain.settings ?? {}
        },
        geoSettings(): GeoSettings {
            const geoSettings = getSetting(this.settings, 'geo') ?? null

            return {
                disabled: (this.isGroupSite || geoSettings?.disabled) ?? false,
                defaultLocationId: geoSettings?.defaultLocationId ?? null,
                precision: geoSettings?.precision ?? null,
            }
        },
        getAnalyticsSettings(): Record<string, any> | null {
            const analytics: Record<string, any> | null =
                this.settings.vendor?.analytics ?? null

            if (!analytics) {
                return null
            }

            return buildAnalyticsSettings(analytics)
        },
        measurementIds(): string[] | null {
            return (
                getSetting(
                    this.settings,
                    'vendor.analytics.google.analytics',
                ) ?? null
            )
        },
        zarazIsEnabled() {
            return (
                getSetting(this.settings, 'vendor.analytics.zaraz.enabled') ??
                false
            )
        },
        inventorySettings(): InventorySettings {
            const inventorySettings: Record<string, any> = getSetting(
                this.settings,
                'inventory',
            )

            return {
                overlay: getSetting(inventorySettings, 'images.overlay'),
                hidePrices: getSetting(
                    inventorySettings,
                    'images.hidePrices',
                    false,
                ),
                unlockPrices: getSetting(
                    inventorySettings,
                    'images.unlockPrices',
                    false,
                ),
                unlockPriceType: getSetting(
                    inventorySettings,
                    'images.unlockPriceType',
                ),
                srpGallery: getSetting(inventorySettings, 'srp.gallery', false),
                disableImageCrop: getSetting(
                    inventorySettings,
                    'images.disableCrop',
                    false,
                ),
                callForPrice: {
                    new: getSetting(inventorySettings, 'callForPrice.new'),
                    used: getSetting(inventorySettings, 'callForPrice.used'),
                },
                makeOrder: getSetting(inventorySettings, 'srp.makeOrder', []),
                vdpHistoryDisabled: getSetting(
                    inventorySettings,
                    'vdp.historyDisabled',
                    false,
                ),
                vdpGalleryOptions: mapVdpGalleryType(inventorySettings),
            } as InventorySettings
        },
        oemOptionsDisplay(): OemOptionDisplaySettings {
            const oemOptionSettings: Record<string, any> =
                getSetting(this.autoZnetworkSettings, 'inventory.oemOptions') ??
                {}

            return {
                disabled: oemOptionSettings?.disabled ?? false,
                hideValues: oemOptionSettings?.hideValues ?? false,
            }
        },
        gtagIsEnabled() {
            return (
                !this.zarazIsEnabled &&
                this.measurementIds &&
                this.measurementIds.length > 0
            )
        },
        pricesHidden(): boolean {
            return getSetting(this.settings, 'inventory.hidePrices') ?? false
        },
        priceUnlock(): boolean {
            return getSetting(this.settings, 'inventory.unlockPrices') ?? false
        },
        shouldHidePrice(): boolean {
            return this.pricesHidden || this.priceUnlock
        },
        isGroupSite(): boolean {
            return (
                this.autoZnetworkEnterprise !== null &&
                !this.autoZnetworkEnterprise.physical
            )
        },
        cargurusEnabled(): boolean {
            return getSetting(this.settings, 'inventory.cargurus.enabled')
        },
        oemIncentivesEnabled(): boolean {
            return getSetting(this.settings, 'oem.incentives.enabled') ?? false
        },
        complyAuto(): ComplyAuto {
            const complyAutoSettings: Record<string, any> = getSetting(
                this.settings,
                'complyAuto',
            )

            return {
                enabled: complyAutoSettings?.enabled ?? false,
                ids: {
                    cookieBanner: complyAutoSettings?.cookieBanner ?? null,
                    privacyPolicy: complyAutoSettings?.privacyPolicy ?? null,
                    requestPortal: complyAutoSettings?.requestPortal ?? null,
                },
            }
        },
        honkr(): Honkr {
            const honkr: Record<string, any> | null =
                getSetting(this.settings, 'honkr') ?? null

            const amounts = honkr?.deposits?.amounts ?? {}

            return {
                organizationId: honkr?.organizationId ?? null,
                deposits: {
                    enabled: honkr?.deposits?.enabled ?? false,
                    type: honkr?.deposits?.type ?? null,
                    amounts: {
                        standard: amounts.standard ?? null,
                        alternative: amounts.alternative ?? null,
                    },
                },
            }
        },
        chatmonsterSettings(): ChatmonsterSettings {
            const chatmonsterSettings: Record<string, any> = getSetting(
                this.settings,
                'chatmonster',
            )

            return {
                chat: {
                    enabled: chatmonsterSettings?.chat?.enabled ?? false,
                },
            } as ChatmonsterSettings
        },
        payments(): PaymentSettings {
            const paymentSettings: Record<string, any> | null =
                getSetting(this.settings, 'payments') ?? null

            return {
                enabled: paymentSettings?.enabled ?? true,
                prompt: paymentSettings?.prompt ?? false,
                docTitleFees: {
                    title: paymentSettings?.docTitleFees?.titleAmount ?? null,
                    doc: paymentSettings?.docTitleFees?.docAmount ?? null,
                    combined:
                        paymentSettings?.docTitleFees?.combinedAmount ?? null,
                },
                defaults: {
                    creditRanges: paymentSettings?.creditRanges ?? null,
                    termMonths: paymentSettings?.termMonths ?? null,
                    tier: paymentSettings?.tier ?? null,
                    termLength: paymentSettings?.termLength ?? null,
                    vehicleDownPercentage:
                        paymentSettings?.downPercentage ?? 10,
                },
            }
        },
        bureau(state): Bureau | null {
            return state.domain.finance?.bureau ?? null
        },
        bureauName() {
            switch (this.bureau) {
                case 'TU':
                    return 'TransUnion'
                case 'EFX':
                    return 'Equifax'
                case 'XPN':
                    return 'Experian'
                default:
                    return null
            }
        },
        getTheme(): string {
            return `:root,
        :before,
        :after,
        *
            {
            ${buildTheme(this.theme)}
        }`
        },
        defaultInventorySort(): string | null {
            return (
                getSetting(this.settings, 'inventory.srp.defaultSort') ?? null
            )
        },
    },
    actions: {
        setDomain(domain: Domain, entityType: DomainEntityType | null) {
            this.domain = domain
            this.entityType = entityType
        },
    },
})

function buildTheme(theme: Theme): string {
    const variables = theme.variables

    return (
        Object.keys(variables)
            .map((key) => `--${key}: ${variables[key]}`)
            .join(';') + ';'
    )
}

function mapVdpGalleryType(inventorySettings: Record<string, any>): VDPGallery {
    const type: string | null = getSetting(
        inventorySettings,
        'vdp.gallery.type',
    )

    let vdpType: VDPGalleryType = VDPGalleryType.DEFAULT
    let id: string | number | null = null

    switch (type) {
        case VDPGalleryType.DEALER_IMAGE_PRO:
            vdpType = VDPGalleryType.DEALER_IMAGE_PRO
            id = getSetting(inventorySettings, 'vdp.gallery.dealerImageProId')
            break
    }

    return {
        type: vdpType,
        id,
    }
}

interface State {
    domain: Domain
    entityType: DomainEntityType | null
}

interface InventorySettings {
    overlay: string | null
    hidePrices: boolean
    unlockPrices: boolean
    unlockPriceType?: 'phone_number' | 'email' | undefined
    srpGallery: boolean
    disableImageCrop: boolean
    callForPrice: InventorySettingsCallForPrice
    makeOrder: string[]
    vdpHistoryDisabled: boolean
    vdpGalleryOptions: VDPGallery
}

interface OemOptionDisplaySettings {
    disabled: boolean
    hideValues: boolean
}

interface ChatmonsterSettings {
    chat: ChatmonsterChatSettings
}

interface ChatmonsterChatSettings {
    enabled: boolean
}

interface GeoSettings {
    disabled: boolean
    defaultLocationId: number | null
    precision: number | null
}

interface InventorySettingsCallForPrice {
    new: string | null
    used: string | null
}

interface PaymentSettings {
    enabled: boolean
    prompt: boolean
    docTitleFees: PaymentDocTitleFees
    defaults: PaymentSettingDefaults
}

interface PaymentSettingDefaults {
    creditRanges: CreditScoreRange[] | null
    termMonths: number[] | null
    tier: CreditScoreTier | null
    termLength: number | null
    vehicleDownPercentage: number
}

interface PaymentDocTitleFees {
    title: number | null
    doc: number | null
    combined: number | null
}

interface ComplyAuto {
    enabled: boolean
    ids: ComplyAutoIds
}

interface ComplyAutoIds {
    cookieBanner: string | null
    privacyPolicy: string | null
    requestPortal: string | null
}
